// File: edgelexapp/webelieve-frontend/src/components/lex/ConversationHistoryModal.tsx
import React, { useState, useEffect } from 'react';
import { X, Clock, MessageSquare, Calendar, Tag, FileText, Trash2 } from 'lucide-react';
import { useKeycloak } from '@react-keycloak/web';

export interface ConversationSummary {
  id: string;
  documentId: string;
  sessionId: string;
  conversationId: string;
  summaryText: string;
  messageCount: number;
  sessionDurationSeconds: number;
  startedAt: Date;
  endedAt?: Date;
  importanceLevel: 'routine' | 'high_profile' | 'compliance';
  tags: string[];
  formattedDuration: string;
  sessionStatus: string;
}

export interface DocumentTimeSummary {
  documentId: string;
  realmId: string;
  userId: string;
  totalSessions: number;
  totalDurationSeconds: number;
  averageSessionDurationSeconds: number;
  firstSessionStarted: Date;
  lastSessionEnded?: Date;
  completedSessions: number;
  interruptedSessions: number;
  formattedTotalDuration: string;
  formattedAverageDuration: string;
}

interface ConversationHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  documentTitle?: string;
  onLoadConversation?: (sessionId: string, conversationId: string) => void;
}

export const ConversationHistoryModal: React.FC<ConversationHistoryModalProps> = ({
  isOpen,
  onClose,
  documentId,
  documentTitle = 'Document',
  onLoadConversation
}) => {
  const { keycloak } = useKeycloak();
  const [summaries, setSummaries] = useState<ConversationSummary[]>([]);
  const [timeSummary, setTimeSummary] = useState<DocumentTimeSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<'all'>('all');
  const refreshDebounceRef = React.useRef<number | null>(null);

  // Load conversation history when modal opens and refresh every 30 seconds for real-time updates (debounced)
  useEffect(() => {
    if (isOpen && documentId) {
      loadConversationHistory();
      loadTimeSummary();
      
      // Set up interval to refresh data every 30 seconds for real-time duration updates (debounced)
      const interval = setInterval(() => {
        loadConversationHistory();
        loadTimeSummary();
      }, 30000); // Increased from 5 seconds to 30 seconds to reduce flickering
      
      return () => clearInterval(interval);
    }
  }, [isOpen, documentId]);

  // Listen for external refresh events, with debounce protection
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent)?.detail as { documentId?: string } | undefined;
      if (detail?.documentId && detail.documentId !== documentId) return;
      if (!isOpen) return;
      if (refreshDebounceRef.current) {
        window.clearTimeout(refreshDebounceRef.current);
        refreshDebounceRef.current = null;
      }
      refreshDebounceRef.current = window.setTimeout(() => {
        loadConversationHistory();
        loadTimeSummary();
      }, 250); // small debounce to avoid burst reloads
    };
    window.addEventListener('lex:refreshHistory', handler as EventListener);
    return () => {
      window.removeEventListener('lex:refreshHistory', handler as EventListener);
      if (refreshDebounceRef.current) {
        window.clearTimeout(refreshDebounceRef.current);
        refreshDebounceRef.current = null;
      }
    };
  }, [isOpen, documentId]);

  const loadConversationHistory = async () => {
    if (!keycloak.authenticated || !keycloak.token) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/v1/lex-agent/document/${documentId}/history`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load conversation history: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.data.summaries) {
        // Convert string dates to Date objects
        const processedSummaries = data.data.summaries.map((summary: any) => ({
          ...summary,
          startedAt: new Date(summary.startedAt),
          endedAt: summary.endedAt ? new Date(summary.endedAt) : undefined
        }));
        
        setSummaries(processedSummaries);
        console.log(`📄 [ConversationHistoryModal] Loaded ${processedSummaries.length} conversation summaries`);
      }
    } catch (err) {
      console.error('🚨 [ConversationHistoryModal] Error loading conversation history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load conversation history');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTimeSummary = async () => {
    if (!keycloak.authenticated || !keycloak.token) return;

    try {
      const response = await fetch(`/api/v1/lex-agent/document/${documentId}/time-summary`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setTimeSummary(data.data);
        }
      }
    } catch (err) {
      console.error('🚨 [ConversationHistoryModal] Error loading time summary:', err);
    }
  };

  const handleLoadConversation = (sessionId: string, conversationId: string) => {
    if (onLoadConversation) {
      onLoadConversation(sessionId, conversationId);
      onClose();
    }
  };

  const handleDeleteSession = async (sessionId: string, conversationId: string) => {
    if (!keycloak.authenticated || !keycloak.token) return;

    try {
      console.log('🗑️ [ConversationHistoryModal] Deleting session:', sessionId);
      
      // Delete the conversation summary
      const response = await fetch(`/api/v1/lex-agent/conversation/${conversationId}/delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log('✅ [ConversationHistoryModal] Session deleted successfully');
        // Reload the conversation history to reflect the deletion
        loadConversationHistory();
        loadTimeSummary();
      } else {
        console.error('🚨 [ConversationHistoryModal] Failed to delete session:', response.status);
      }
    } catch (error) {
      console.error('🚨 [ConversationHistoryModal] Error deleting session:', error);
    }
  };

  const getFilteredSummaries = () => {
    // Only show all summaries since we removed the active filter
    return summaries;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleString();
  };

  // Calculate real-time duration for active sessions
  const getRealTimeDuration = (summary: ConversationSummary) => {
    // Completed sessions: prefer stored duration
    if (summary.sessionStatus?.toLowerCase() !== 'active') {
      if (summary.sessionDurationSeconds && summary.sessionDurationSeconds > 0) {
        return formatDuration(summary.sessionDurationSeconds);
      }
      // If endedAt and startedAt exist but duration missing, compute client-side safely
      if (summary.startedAt && summary.endedAt) {
        const start = new Date(summary.startedAt).getTime();
        const end = new Date(summary.endedAt).getTime();
        if (end > start) return formatDuration(Math.floor((end - start) / 1000));
      }
      return summary.formattedDuration || 'Unknown duration';
    }

    // Active session: compute real-time duration since start
    if (summary.startedAt) {
      const startTime = new Date(summary.startedAt).getTime();
      const currentTime = Date.now();
      const durationSeconds = Math.floor((currentTime - startTime) / 1000);
      return formatDuration(durationSeconds);
    }
    return 'Unknown duration';
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  const getImportanceIcon = (importanceLevel: string) => {
    switch (importanceLevel) {
      case 'high_profile':
        return <Tag className="w-4 h-4 text-red-500" />;
      case 'compliance':
        return <Tag className="w-4 h-4 text-orange-500" />;
      default:
        return <Tag className="w-4 h-4 text-gray-400" />;
    }
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <FileText className="w-6 h-6 text-lex-accent" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Conversation History</h2>
              <p className="text-sm text-gray-500">{documentTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Time Summary */}
        {timeSummary && (
          <div className="bg-lex-accent-soft p-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Clock className="w-5 h-5 text-lex-accent" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Total Time Worked</p>
                  <p className="text-lg font-bold text-lex-accent">{timeSummary.formattedTotalDuration}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">{timeSummary.totalSessions} sessions</p>
                <p className="text-sm text-gray-600">Avg: {timeSummary.formattedAverageDuration}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex space-x-2">
            <button
              onClick={() => setSelectedFilter('all')}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                selectedFilter === 'all'
                  ? 'bg-lex-accent-soft text-lex-accent'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All ({summaries.length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0" style={{ maxHeight: '300px' }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
              <span className="ml-3 text-gray-600">Loading conversation history...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-2">Error loading conversation history</p>
              <p className="text-sm text-gray-500">{error}</p>
              <button
                onClick={loadConversationHistory}
                className="mt-4 px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
              >
                Retry
              </button>
            </div>
          ) : getFilteredSummaries().length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No conversation history found</p>
              <p className="text-sm text-gray-500 mt-1">Start a conversation to see history here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {getFilteredSummaries().map((summary) => (
                <div
                  key={summary.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-lex-accent transition-colors cursor-pointer"
                  onClick={() => handleLoadConversation(summary.sessionId, summary.conversationId)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        {getImportanceIcon(summary.importanceLevel)}
                        <span className="text-xs text-gray-500">
                          {summary.messageCount} messages
                        </span>
                      </div>
                      
                      <p className="text-gray-900 mb-2 line-clamp-2">
                        {summary.summaryText}
                      </p>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(summary.startedAt)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>{getRealTimeDuration(summary)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end space-y-1">
                      <span className="text-xs text-gray-400">
                        Session {summary.sessionId.slice(-8)}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering the conversation load
                          handleDeleteSession(summary.sessionId, summary.conversationId);
                        }}
                        className="p-1 hover:bg-red-100 rounded transition-colors"
                        title="Delete session"
                      >
                        <Trash2 className="w-4 h-4 text-red-500 hover:text-red-700" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
